import { Text } from '@radix-ui/themes';
import styled, { css } from 'styled-components';

// ------------------------------
// Typography v4
// 2025 -> 🤷
// ------------------------------
// Text
// https://www.figma.com/design/edp8Jm4u5954IDSQJ5utgn/ODS---Components?node-id=800-3750&t=opM2HoWbMwA5M7xI-4

export const InputErrorTextCss = css`
  color: var(--error-a10);
`;

export const InputErrorText = styled(Text)`
  ${InputErrorTextCss}
`;

export const InputHelpText = styled(Text)`
  color: var(--neutral-a9);
`;

// ------------------------------
// Typography v3
// 2024 -> 2025
// ------------------------------
// Text
// https://www.figma.com/design/edp8Jm4u5954IDSQJ5utgn/ODS---Components?node-id=800-3750&t=opM2HoWbMwA5M7xI-4

const textSize1Css = css`
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.48px;
`;

const textSize2Css = css`
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.42px;
`;

const textSize3Css = css`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.32px;
`;

const textSize4Css = css`
  font-size: 18px;
  line-height: 26px;
`;

const textSize5Css = css`
  font-size: 20px;
  line-height: 28px;
`;

const textSize6Css = css`
  font-size: 24px;
  line-height: 30px;
  letter-spacing: -0.1px;
`;

const textSize7Css = css`
  font-size: 28px;
  line-height: 36px;
  letter-spacing: -0.12px;
`;

const textSize8Css = css`
  font-size: 36px;
  line-height: 40px;
  letter-spacing: -0.16px;
`;

const textSize9Css = css`
  font-size: 60px;
  line-height: 60px;
  letter-spacing: -0.4px;
`;

interface LegacyTextProps {
  size?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  weight?: 'regular' | 'medium' | 'bold';
  color?: string;
  highContrast?: boolean;
  align?: 'start' | 'center' | 'end' | 'justify';
}

const textSizesCss = css<LegacyTextProps>`
  ${textSize2Css} /* default */
  ${props => props.size === 1 && textSize1Css}
  ${props => props.size === 3 && textSize3Css}
  ${props => props.size === 4 && textSize4Css}
  ${props => props.size === 5 && textSize5Css}
  ${props => props.size === 6 && textSize6Css}
  ${props => props.size === 7 && textSize7Css}
  ${props => props.size === 8 && textSize8Css}
  ${props => props.size === 9 && textSize9Css}
`;

const textWeightRegularCss = css`
  font-weight: 400;
`;

const textWeightMediumCss = css`
  font-weight: 500;
`;

const textWeightBoldCss = css`
  font-weight: 700;
`;

const textWeightCss = css<LegacyTextProps>`
  ${textWeightRegularCss} /* default */
  ${props => props.weight === 'medium' && textWeightMediumCss}
  ${props => props.weight === 'bold' && textWeightBoldCss}
`;

export const LegacyText = styled.p<LegacyTextProps>`
  ${textSizesCss}
  ${textWeightCss}
  text-align: ${props => props.align || 'start'};

  color: ${props =>
    props.color
      ? props.color
      : props.highContrast
        ? props.theme.color.neutralAlpha[11]
        : props.theme.color.neutral[12]};
`;

// ------------------------------
// Typography v2
// 2024 -> 🤷
// ------------------------------

interface TypographyProps {
  size?: 'small' | 'medium' | 'large';
  weight?: 'regular' | 'medium';
  type?: 'display' | 'headline' | 'title' | 'body' | 'label';
  color?: string;
}

const weightRegularCss = css`
  font-weight: 400;
`;

const weightMediumCss = css`
  font-weight: 500;
`;

const weightCss = css<TypographyProps>`
  ${weightRegularCss}
  ${props => props.weight === 'medium' && weightMediumCss}
`;

const displayLargeCss = css`
  font-size: 48px; /* 3rem */
  line-height: 60px; /* 3.75rem */
`;

const displayMediumCss = css`
  font-size: 36px; /* 2.25rem */
  line-height: 44px; /* 2.75rem */
`;

const displaySmallCss = css`
  font-size: 32px; /* 2rem */
  line-height: 40px; /* 2.5rem */
`;

const displayCss = css<TypographyProps>`
  ${displayMediumCss}
  ${props => props.size === 'large' && displayLargeCss}
  ${props => props.size === 'small' && displaySmallCss}
`;

const headlineLargeCss = css`
  font-size: 28px; /* 1.75rem */
  line-height: 36px; /* 2.25rem */
`;

const headlineMediumCss = css`
  font-size: 24px; /* 1.5rem */
  line-height: 32px; /* 2rem */
`;

const headlineSmallCss = css`
  font-size: 20px; /* 1.25rem */
  line-height: 30px; /* 1.875rem */
`;

const headlineCss = css<TypographyProps>`
  ${headlineMediumCss}
  ${props => props.size === 'large' && headlineLargeCss}
  ${props => props.size === 'small' && headlineSmallCss}
`;

const titleLargeCss = css`
  font-size: 18px; /* 1.125rem */
  line-height: 28px; /* 1.75rem */
`;

const titleMediumCss = css`
  font-size: 16px; /* 1rem */
  line-height: 24px; /* 1.5rem */
`;

const titleSmallCss = css`
  font-size: 14px; /* 0.875rem */
  line-height: 20px; /* 1.25rem */
`;

const titleCss = css<TypographyProps>`
  ${titleMediumCss}
  ${props => props.size === 'large' && titleLargeCss}
  ${props => props.size === 'small' && titleSmallCss}
`;

const bodyLargeCss = css`
  font-size: 16px; /* 1rem */
  line-height: 24px; /* 1.5rem */
`;

const bodyMediumCss = css`
  font-size: 14px; /* 0.875rem */
  line-height: 20px; /* 1.25rem */
`;

const bodySmallCss = css`
  font-size: 12px; /* 0.75rem */
  line-height: 18px; /* 1.125rem */
`;

const bodyCss = css<TypographyProps>`
  ${bodyMediumCss}
  ${props => props.size === 'large' && bodyLargeCss}
  ${props => props.size === 'small' && bodySmallCss}
`;

const labelLargeCss = css`
  font-size: 14px; /* 0.875rem */
  line-height: 20px; /* 1.25rem */
`;

const labelMediumCss = css`
  font-size: 12px; /* 0.75rem */
  line-height: 18px; /* 1.125rem */
`;

const labelSmallCss = css`
  font-size: 11px; /* 0.6875rem */
  line-height: 18px; /* 1.125rem */
`;

const labelCss = css<TypographyProps>`
  ${labelMediumCss}
  ${props => props.size === 'large' && labelLargeCss}
  ${props => props.size === 'small' && labelSmallCss}

  letter-spacing: 2%;
`;

export const Typography = styled.p<TypographyProps>`
  ${weightCss}
  ${bodyCss}
  ${props => props.type === 'display' && displayCss}
  ${props => props.type === 'headline' && headlineCss}
  ${props => props.type === 'title' && titleCss}
  ${props => props.type === 'label' && labelCss}
  ${props => props.color && `color: ${props.color}`}
`;

// ------------------------------
// Typography v1
// 👶 -> 2023
// ------------------------------

interface HelpTextCssProps {
  error?: boolean;
  disabled?: boolean;
}

export const HelpTextCss = css<HelpTextCssProps>`
  margin-top: 2px;
  margin-left: 5px;
  line-height: 16px;
  color: ${props => {
    if (props.error) return props.theme.danger;
    if (props.disabled) return props.theme.lightMedium;
    return props.theme.darkMedium;
  }};
  font-size: 12px;
`;

export const HelpText = styled.p<HelpTextCssProps>`
  ${HelpTextCss}
`;

export const HelpTextLink = styled.p<HelpTextCssProps>`
  ${HelpTextCss}
  margin-left: 3px;
  color: ${props => props.theme.orange};
`;

export const tagLabelCss = css`
  color: ${props => props.theme.darkest};
  font-style: normal;
  font-size: 10px;
  line-height: 15px;
`;

export const TagLabel = styled.p`
  ${tagLabelCss}
`;

export const toolTipTextCss = css`
  color: ${props => props.theme.lightest};
`;

export const ToolTipText = styled.p`
  font-size: 12px;
  ${toolTipTextCss}
`;

export const CTATitleCss = css`
  color: ${props => props.theme.orange};
  font-size: 14px;
  line-height: 17px;
  font-weight: 700;
`;

export const CTATitle = styled.p`
  ${CTATitleCss}
`;

export const CTASubtitleCss = css`
  color: ${props => props.theme.lightest};
  font-weight: 700;
  font-size: 28px;
  line-height: 33px;
  margin-top: 20px;
`;

export const CTASubtitle = styled.p`
  ${CTASubtitleCss}
`;

export const CTAListTitleCss = css`
  color: ${props => props.theme.lightest};
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
`;

export const CTAListTitle = styled.p`
  ${CTAListTitleCss}
`;

export const CTALinkCss = css`
  color: ${props => props.theme.copperLightest};
  font-size: 13px;
  line-height: 16px;
  text-decoration: underline;
  text-underline-offset: 6px;

  &:hover {
    color: ${props => props.theme.copperDarkest};
  }
`;

export const CTALink = styled.p`
  ${CTALinkCss}
`;

export const SimPageCTATextCss = css`
  color: ${props => props.theme.copperDarkest};
  font-size: 14px;
  line-height: 27px;
`;

export const CTAListItemCss = css`
  color: ${props => props.theme.lightest};
  font-size: 16px;
  line-height: 25px;
`;
export const CTAListItem = styled.li`
  ${CTAListItemCss}
`;

export const CTAParagraph = styled.p`
  color: ${props => props.theme.lightest};
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
`;

export const CTAEmphasize = styled.span`
  font-weight: 700;
  color: ${props => props.theme.copperLightest};
`;

export const ModalParagraph = styled.p`
  line-height: 23px;

  & a {
    cursor: pointer;
  }
`;

export const ModalTitleCss = css`
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  color: ${props => props.theme.darker};
`;

export const ModalTitle = styled.p`
  ${ModalTitleCss}
`;

export const signUpBigTitleCss = css`
  color: #fff;
  font-size: 3.875rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

export const SignUpBigTitle = styled.span`
  ${signUpBigTitleCss}
  @media (max-width: 48rem) {
    display: none;
  }
`;

export const SignUpCardTitle = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 8px;
  line-height: 30px;
  letter-spacing: -0.1px;
  text-align: center;
`;

export const signUpSubtitleCss = css`
  text-align: center;
`;

export const SignUpCardSubtitle = styled.div`
  ${signUpSubtitleCss}
  color: ${props => props.theme.darkMedium};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.42px;
  @media (max-height: 900px) {
    margin-bottom: 0.5rem;
  }
`;

export const ColorOrange = styled.span`
  color: var(--accent-9);
`;
